import { industryPriorities } from '../data/industryPriorities';
import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import ZildLogo from "./ZildLogo";
import { SECURITY_PORTAL_URL } from "../data/sitePages";
import {
    LANGUAGE_OPTIONS,
    buildLanguageHref,
    getLocaleFromPathname,
    readLocaleValue,
    withLocale,
} from "../lib/locale";

const l = (pt, en) => ({ "pt-BR": pt, "en-US": en });

const MEGA_MENUS = {
    platform: {
        eyebrow: l("Plataforma", "Platform"),
        title: l("Uma camada para operar agentes com controle.", "One layer to operate AI agents with control."),
        intro: l(
            "Admin define limites. Assist, Voice e Chat operam com contexto. Insight, Flow e Coach fecham o ciclo.",
            "Admin sets boundaries. Assist, Voice and Chat operate with context. Insight, Flow and Coach close the loop."
        ),
        columns: [
            {
                label: l("Plataforma e gestão", "Platform and management"),
                items: [
                    { label: l("Visão geral", "Overview"), body: l("Como os sete módulos trabalham juntos.", "How the seven modules work together."), path: "/platform" },
                    { label: "Zild Admin", body: l("Permissões, políticas, modelos e auditoria.", "Permissions, policies, models and audit."), path: "/products/admin" },
                    { label: "Zild Chat", body: l("GPT interno seguro para colaboradores.", "A secure internal GPT for employees."), path: "/products/chat" },
                ],
            },
            {
                label: l("Processos e inteligência", "Processes and intelligence"),
                items: [
                    { label: "Zild Flow", body: l("Tarefas associadas a pessoas ou agentes de IA.", "Tasks assigned to people or AI agents."), path: "/products/flow" },
                    { label: "Zild Insight", body: l("Qualidade, custo, risco e tendência.", "Quality, cost, risk and trend visibility."), path: "/products/insight" },
                ],
            },
            {
                label: l("Atendimento e capacitação", "Service and training"),
                items: [
                    { label: "Zild Assist", body: l("Atendimento em canais digitais com handoff humano.", "Digital service channels with human handoff."), path: "/products/assist" },
                    { label: "Zild Voice", body: l("Voz, recepção, callback e monitoria.", "Voice, reception, callback and monitoring."), path: "/products/voice" },
                    { label: "Zild Coach", body: l("Treinamento, simulação e coaching.", "Training, simulation and coaching."), path: "/products/coach" },
                ],
            },
        ],
        note: l("Agente quando é seguro. Humano quando importa.", "Agent when it is safe. Human when it matters."),
    },
    departments: {
        eyebrow: l("Departamentos", "Departments"),
        title: l("O trabalho flui quando as áreas se conectam.", "Work flows when departments connect."),
        intro: l("Explore como pessoas e agentes conectam a demanda, a execução e o resultado da empresa.", "Explore how people and agents connect demand, execution and business results."),
        columns: [{ label: l("Receita e relacionamento","Revenue and relationships"), items: [{ label: l("Marketing","Marketing"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#marketing" },{ label: l("Comercial","Commercial"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#commercial" },{ label: l("Vendas","Sales"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#sales" },{ label: l("Financeiro","Finance"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#finance" }] },{ label: l("Operação e entrega","Operations and delivery"), items: [{ label: l("Produção","Production"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#production" },{ label: l("Compras","Purchasing"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#purchasing" },{ label: l("Estoque","Inventory"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#inventory" },{ label: l("Logística","Logistics"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#logistics" }] },{ label: l("Gestão e suporte","Management and support"), items: [{ label: l("Fiscal","Tax"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#tax" },{ label: l("Contábil","Accounting"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#accounting" },{ label: l("TI","IT"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#it" },{ label: l("RH","HR"), body: l("Fluxos e conexões da área.", "Department workflows and connections."), path: "/departments#hr" }] }],
        note: l("Da primeira oportunidade ao fluxo de caixa, com contexto compartilhado.", "From the first opportunity to cash flow, with shared context."),
    },
    industries: {
        eyebrow: l("Indústrias", "Industries"),
        title: l("IA aplicada ao contexto da sua operação.", "AI applied to your operating context."),
        intro: l("Encontre oportunidades para conectar conhecimento, documentos e processos no seu setor.", "Find opportunities to connect knowledge, documents and processes in your industry."),
        columns: [
            { label: l("Produção e infraestrutura", "Production and infrastructure"), items: industryPriorities.slice(0,3).map(item => ({label:l(item.name,item.nameEn),body:l(item.meta,item.metaEn),path:`/industries/${item.key}`})) },
            { label: l("Comércio e serviços", "Commerce and services"), items: industryPriorities.slice(3,6).map(item => ({label:l(item.name,item.nameEn),body:l(item.meta,item.metaEn),path:`/industries/${item.key}`})) },
            { label: l("Energia e mais", "Energy and more"), items: [
                ...industryPriorities.slice(6).map(item => ({label:l(item.name,item.nameEn),body:l(item.meta,item.metaEn),path:`/industries/${item.key}`})),
                {label:l("Todas as indústrias", "All industries"),body:l("Explore as oportunidades no seu setor.", "Explore opportunities in your industry."),path:"/industries"},
                {label:l("Consultoria em IA", "AI consulting"),body:l("Da escolha do processo à adoção em escala.", "From choosing a process to adoption at scale."),path:"/implementation"}
            ] }
        ],
        note: l("Cada setor tem seu contexto. Cada fluxo tem seus responsáveis.", "Every industry has its context. Every workflow has its owners."),
    },
    resources: {
        eyebrow: l("Recursos", "Resources"),
        title: l("Conhecimento e apoio para avançar com IA.", "Knowledge and support to move forward with AI."),
        intro: l("Explore conteúdos, planeje sua adoção e encontre documentação e suporte.", "Explore content, plan adoption and find documentation and support."),
        columns: [
            { label: l("Conhecimento", "Knowledge"), items: [
                { label: l("Guia de Uso Seguro de IA", "Guide to Safe AI Use"), body: l("Dados, permissões e supervisão no uso de IA.", "Data, permissions and oversight when using AI."), path: "/guia-de-uso-seguro-de-ia" },
                { label: l("Sobre", "About"), body: l("Nossa visão para a adoção corporativa de IA.", "Our vision for enterprise AI adoption."), path: "/about" },
                { label: l("Blog e novidades", "Blog and updates"), body: l("Lançamentos Zild e notícias dos provedores de IA.", "Zild releases and AI provider news."), path: "/blog" },
                { label: l("Estudos e guias", "Studies and guides"), body: l("Conteúdos práticos para gestores de IA.", "Practical content for AI leaders."), path: "/studies" },
            ] },
            { label: l("Adoção de IA", "AI adoption"), items: [
                { label: l("Jornadas por perfil", "Paths by role"), body: l("Desenvolvimento, operações, engenharia e finanças.", "Development, operations, engineering and finance."), path: "/journeys" },
                { label: l("COE de IA", "AI Center of Excellence"), body: l("Organize padrões, pessoas e resultados.", "Organize standards, people and outcomes."), path: "/journeys/ai-coe" },
                { label: l("Calculadora de TCO e ROI", "TCO and ROI calculator"), body: l("Compare custos e estime o retorno da IA.", "Compare costs and estimate AI returns."), path: "/tools/ai-economics" },
            ] },
            { label: l("Suporte e documentação", "Support and documentation"), items: [
                { label: l("Documentação", "Documentation"), body: l("Guias e referências da plataforma.", "Platform guides and references."), path: "/docs" },
                { label: l("Portal de Segurança", "Trust Portal"), body: l("Controles, conformidade e documentos de segurança.", "Security controls, compliance and documentation."), href: SECURITY_PORTAL_URL },
                { label: l("Suporte", "Support"), body: l("Canais para clientes em operação.", "Channels for customers in operation."), path: "/support" },
            ] },
        ],
        note: l("Da descoberta à operação, com apoio em cada etapa.", "From discovery to operations, with support at every step."),
    },
};

const NAV = [
    { key: "platform", path: "/platform", label: l("Plataforma", "Platform"), menu: MEGA_MENUS.platform },
    { key: "industries", path: "/industries", label: l("Indústrias", "Industries"), menu: MEGA_MENUS.industries },
    { key: "departments", path: "/departments", label: l("Departamentos", "Departments"), menu: MEGA_MENUS.departments },
    { key: "resources", path: "/studies", label: l("Recursos", "Resources"), menu: MEGA_MENUS.resources },
    { key: "pricing", path: "/precos", label: l("Preços", "Pricing") },
];

const MOBILE_SECONDARY = [
    { path: "/guia-de-uso-seguro-de-ia", label: l("Guia de Uso Seguro de IA", "Guide to Safe AI Use") },
    { path: "/blog", label: l("Blog e novidades", "Blog and updates") },
    { path: "/about", label: l("Sobre", "About") },
    { path: "/studies", label: l("Estudos e guias", "Studies and guides") },
    { path: "/journeys", label: l("Jornadas por perfil", "Paths by role") },
    { path: "/journeys/ai-coe", label: l("COE de IA", "AI Center of Excellence") },
    { path: "/tools/ai-economics", label: l("Calculadora de TCO e ROI", "TCO and ROI calculator") },
    { path: "/implementation", label: l("Serviços", "Services") },
    { href: SECURITY_PORTAL_URL, label: l("Portal de Segurança", "Trust Portal") },
    { path: "/support", label: l("Suporte", "Support") },
];

const SiteHeader = () => {
    const { pathname, search, hash } = useLocation();
    const [open, setOpen] = useState(false);
    const [activeMenu, setActiveMenu] = useState(null);
    const currentLocale = getLocaleFromPathname(pathname);
    const locale = `/${currentLocale}`;
    const activeNav = useMemo(() => NAV.find((item) => item.key === activeMenu), [activeMenu]);

    const localized = (itemOrPath) => {
        const path = typeof itemOrPath === "string" ? itemOrPath : itemOrPath.path;
        return withLocale(path, currentLocale);
    };

    const switchHref = (targetLocale) => buildLanguageHref({ pathname, search, hash }, targetLocale);

    const closeMenus = () => {
        setOpen(false);
        setActiveMenu(null);
    };

    const LanguageSwitch = ({ className = "", location = "site_header" }) => (
        <div
            className={`zild-language-switch ${className}`}
            data-testid="language-switch"
            data-no-translate="true"
            role="group"
            aria-label={currentLocale === "pt-BR" ? "Selecionar idioma" : "Select language"}
        >
            {LANGUAGE_OPTIONS.map((option) => {
                const active = option.code === currentLocale;
                return (
                    <Link
                        key={option.code}
                        to={switchHref(option.code)}
                        hrefLang={option.code}
                        aria-current={active ? "true" : undefined}
                        aria-label={option.label}
                        title={option.label}
                        data-testid={`language-switch-${option.code === "pt-BR" ? "pt" : "en"}`}
                        data-analytics-event={active ? undefined : "language_switch_click"}
                        data-analytics-label={`Switch language to ${option.label}`}
                        data-analytics-location={location}
                        data-analytics-locale-from={currentLocale}
                        data-analytics-locale-to={option.code}
                        data-analytics-skip={active ? "true" : undefined}
                    >
                        {option.short}
                    </Link>
                );
            })}
        </div>
    );

    useEffect(() => {
        setOpen(false);
        setActiveMenu(null);
        document.documentElement.lang = currentLocale;
    }, [pathname, hash, currentLocale]);

    useEffect(() => {
        if (!open && !activeMenu) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
                setActiveMenu(null);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, activeMenu]);

    return (
        <header
            data-testid="site-header"
            onMouseLeave={() => setActiveMenu(null)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setActiveMenu(null);
            }}
            style={{
                position: "sticky",
                top: 0,
                zIndex: 50,
                background: "rgba(247, 245, 242, 0.88)",
                backdropFilter: "saturate(140%) blur(12px)",
                WebkitBackdropFilter: "saturate(140%) blur(12px)",
                borderBottom: "1px solid var(--zild-border-soft)",
            }}
        >
            <div
                className="zild-container"
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    height: 68,
                }}
            >
                <Link to={locale} data-testid="header-logo-link" aria-label="Zild">
                    <ZildLogo variant="ink" size={36} />
                </Link>

                <nav aria-label="Primary navigation" className="zild-nav-desktop">
                    {NAV.map((item) => (
                        <div
                            key={item.path}
                            className="zild-nav-item"
                            onMouseEnter={() => setActiveMenu(item.menu ? item.key : null)}
                        >
                            <NavLink
                                to={localized(item)}
                                data-testid={`nav-link-${item.path.replace(/\//g, "")}`}
                                className={({ isActive }) =>
                                    `zild-nav-link${isActive || activeMenu === item.key ? " zild-nav-link--active" : ""}`
                                }
                                onFocus={() => setActiveMenu(item.menu ? item.key : null)}
                            >
                                {readLocaleValue(item.label, currentLocale)}
                                {item.menu ? <ChevronDown size={14} strokeWidth={1.8} aria-hidden="true" /> : null}
                            </NavLink>
                        </div>
                    ))}
                </nav>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <LanguageSwitch className="zild-language-switch--desktop" />
                    <Link
                        to={localized({ path: "/login" })}
                        className="zild-btn zild-btn-ghost"
                        data-testid="header-login-link"
                        style={{ display: "none" }}
                    >
                        Login
                    </Link>
                    <Link
                        to={localized({ path: "/contact" })}
                        className="zild-btn zild-btn-primary"
                        data-testid="header-cta-demo"
                    >
                        {currentLocale === "pt-BR" ? "Diagnóstico gratuito" : "Free AI assessment"}
                    </Link>
                    <button
                        type="button"
                        className="zild-btn zild-btn-ghost zild-mobile-toggle"
                        onClick={() => setOpen((o) => !o)}
                        aria-label={currentLocale === "en-US" ? (open ? "Close menu" : "Open menu") : (open ? "Fechar menu" : "Abrir menu")}
                        aria-expanded={open}
                        aria-controls="zild-mobile-menu"
                        data-testid="header-mobile-toggle"
                        style={{ padding: 8 }}
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {activeNav?.menu ? (
                <div className="zild-mega-shell" data-testid="header-mega-menu">
                    <div className="zild-container zild-mega-menu">
                        <div className="zild-mega-menu__intro">
                            <p>{readLocaleValue(activeNav.menu.eyebrow, currentLocale)}</p>
                            <h2>{readLocaleValue(activeNav.menu.title, currentLocale)}</h2>
                            <span>{readLocaleValue(activeNav.menu.intro, currentLocale)}</span>
                        </div>
                        <div className="zild-mega-menu__columns">
                            {activeNav.menu.columns.map((column, columnIndex) => (
                                <div className="zild-mega-menu__column" key={readLocaleValue(column.label, currentLocale)}>
                                    <p>{readLocaleValue(column.label, currentLocale)}</p>
                                    {column.items.map((item, itemIndex) => {
                                        const label = readLocaleValue(item.label, currentLocale);
                                        const content = (
                                            <>
                                                <strong>{label}</strong>
                                                <span>{readLocaleValue(item.body, currentLocale)}</span>
                                            </>
                                        );
                                        const sharedProps = {
                                            className: "zild-mega-menu__item",
                                            "data-mega-item": true,
                                            style: { "--mega-delay": `${(columnIndex * 4 + itemIndex) * 34}ms` },
                                        };

                                        return item.href ? (
                                            <a
                                                key={label}
                                                href={item.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                {...sharedProps}
                                            >
                                                {content}
                                            </a>
                                        ) : (
                                            <Link key={label} to={localized(item.path)} {...sharedProps}>
                                                {content}
                                            </Link>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        <div className="zild-mega-menu__note">{readLocaleValue(activeNav.menu.note, currentLocale)}</div>
                    </div>
                </div>
            ) : null}

            {open && (
                <div
                    id="zild-mobile-menu"
                    className="zild-mobile-menu"
                    data-testid="mobile-menu"
                    style={{
                        borderTop: "1px solid var(--zild-border-soft)",
                        background: "var(--zild-surface)",
                    }}
                >
                    <div className="zild-container" style={{ padding: "16px 24px" }}>
                        <div className="zild-mobile-language-row">
                            <LanguageSwitch className="zild-language-switch--mobile" location="mobile_menu" />
                        </div>
                        {NAV.map((item) => (
                            <NavLink
                                key={item.path}
                                to={localized(item)}
                                onClick={closeMenus}
                                data-testid={`mobile-nav-${item.path.replace(/\//g, "")}`}
                                style={{
                                    display: "block",
                                    padding: "12px 4px",
                                    fontSize: 16,
                                    fontWeight: 500,
                                    color: "var(--zild-text)",
                                    borderBottom: "1px solid var(--zild-border-soft)",
                                }}
                            >
                                {readLocaleValue(item.label, currentLocale)}
                            </NavLink>
                        ))}
                        {MOBILE_SECONDARY.map((item) => {
                            const label = readLocaleValue(item.label, currentLocale);
                            const style = {
                                display: "block",
                                padding: "12px 4px",
                                fontSize: 16,
                                fontWeight: 500,
                                color: "var(--zild-text)",
                                borderBottom: "1px solid var(--zild-border-soft)",
                            };

                            return item.href ? (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={closeMenus}
                                    data-testid="mobile-nav-security-portal"
                                    style={style}
                                >
                                    {label}
                                </a>
                            ) : (
                                <NavLink
                                    key={item.path}
                                    to={localized(item)}
                                    onClick={closeMenus}
                                    data-testid={`mobile-nav-${item.path.replace(/\//g, "")}`}
                                    style={style}
                                >
                                    {label}
                                </NavLink>
                            );
                        })}
                        <Link
                            to={localized({ path: "/login" })}
                            onClick={closeMenus}
                            className="zild-btn zild-btn-secondary"
                            data-testid="mobile-nav-login"
                            style={{
                                width: "100%",
                                marginTop: 14,
                            }}
                        >
                            Login
                        </Link>
                        <Link
                            to={localized({ path: "/contact" })}
                            onClick={closeMenus}
                            className="zild-btn zild-btn-primary"
                            data-testid="mobile-nav-contact"
                            style={{
                                width: "100%",
                                marginTop: 10,
                            }}
                        >
                            {currentLocale === "pt-BR" ? "Diagnóstico gratuito" : "Free AI assessment"}
                        </Link>
                    </div>
                </div>
            )}

            <style>{`
                .zild-nav-desktop {
                    display: none;
                    align-items: center;
                    gap: 2px;
                }

                .zild-nav-item {
                    display: flex;
                    align-items: center;
                }

                .zild-nav-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 5px;
                    font-size: 14px;
                    font-weight: 500;
                    padding: 8px 10px;
                    border-radius: 6px;
                    color: var(--zild-text);
                    transition: color 160ms ease-out, background 160ms ease-out;
                }

                .zild-nav-link:hover,
                .zild-nav-link--active {
                    color: var(--zild-action);
                    background: var(--zild-plum-soft);
                }

                .zild-nav-link svg {
                    transition: transform 160ms ease-out;
                }

                .zild-nav-link--active svg {
                    transform: translateY(1px) rotate(180deg);
                }

                .zild-mega-shell {
                    position: absolute;
                    top: 100%;
                    left: 0;
                    right: 0;
                    background: #faf8f4;
                    border-top: 1px solid var(--zild-border-soft);
                    border-bottom: 1px solid var(--zild-border);
                    box-shadow: 0 22px 42px rgba(16, 43, 48, 0.12);
                    animation: zildMegaPanel 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
                }

                .zild-mega-menu {
                    display: grid;
                    grid-template-columns: minmax(260px, 0.72fr) minmax(0, 1.7fr) minmax(210px, 0.5fr);
                    gap: clamp(28px, 5vw, 72px);
                    padding-top: 28px;
                    padding-bottom: 34px;
                }

                .zild-mega-menu__intro {
                    max-width: 390px;
                }

                .zild-mega-menu__intro p,
                .zild-mega-menu__column > p {
                    margin: 0 0 12px;
                    font-family: var(--zild-font-mono);
                    font-size: 10px;
                    letter-spacing: 0.08em;
                    text-transform: uppercase;
                    color: var(--zild-muted);
                }

                .zild-mega-menu__intro h2 {
                    margin: 0 0 12px;
                    color: var(--zild-aubergine-ink);
                    font-size: 22px;
                    line-height: 1.14;
                    font-weight: 600;
                    letter-spacing: 0;
                }

                .zild-mega-menu__intro span,
                .zild-mega-menu__item span {
                    display: block;
                    color: var(--zild-muted);
                    font-size: 13px;
                    line-height: 1.5;
                }

                .zild-mega-menu__columns {
                    display: grid;
                    grid-template-columns: repeat(3, minmax(0, 1fr));
                    gap: 26px;
                }

                .zild-mega-menu__item {
                    display: block;
                    padding: 8px 0 11px;
                    border-bottom: 1px solid rgba(0, 56, 68, 0.1);
                    opacity: 0;
                    transform: translateY(6px);
                    animation: zildMegaItem 240ms cubic-bezier(0.22, 1, 0.36, 1) forwards;
                    animation-delay: var(--mega-delay);
                }

                .zild-mega-menu__item:hover strong,
                .zild-mega-menu__item:focus-visible strong {
                    color: var(--zild-action);
                }

                .zild-mega-menu__item strong {
                    display: block;
                    margin-bottom: 3px;
                    color: var(--zild-aubergine-ink);
                    font-size: 14px;
                    font-weight: 600;
                    letter-spacing: 0;
                    transition: color 160ms ease-out;
                }

                .zild-mega-menu__note {
                    align-self: end;
                    color: var(--zild-aubergine-ink);
                    font-size: 16px;
                    line-height: 1.45;
                    font-weight: 600;
                    padding-left: 20px;
                    border-left: 1px solid var(--zild-border);
                }

                @keyframes zildMegaPanel {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @keyframes zildMegaItem {
                    to { opacity: 1; transform: translateY(0); }
                }

                .zild-language-switch {
                    display: inline-grid;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                    align-items: center;
                    gap: 2px;
                    padding: 2px;
                    border: 1px solid var(--zild-border-soft);
                    border-radius: 999px;
                    background: rgba(245, 248, 248, 0.72);
                }

                .zild-language-switch a {
                    min-width: 32px;
                    height: 28px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 999px;
                    font-size: 12px;
                    font-weight: 600;
                    line-height: 1;
                    letter-spacing: 0;
                    color: var(--zild-muted);
                    transition: color 160ms ease-out, background 160ms ease-out;
                }

                .zild-language-switch a:hover {
                    color: var(--zild-aubergine-ink);
                }

                .zild-language-switch a[aria-current="true"] {
                    background: var(--zild-aubergine-ink);
                    color: var(--zild-surface);
                    pointer-events: none;
                }

                .zild-language-switch--desktop {
                    display: none;
                }

                .zild-mobile-language-row {
                    display: flex;
                    justify-content: flex-start;
                    padding: 2px 0 14px;
                    border-bottom: 1px solid var(--zild-border-soft);
                    margin-bottom: 2px;
                }

                @media (min-width: 980px) {
                    .zild-nav-desktop { display: flex !important; }
                    .zild-mobile-toggle { display: none !important; }
                    [data-testid="header-login-link"] { display: inline-flex !important; }
                    .zild-language-switch--desktop { display: inline-grid; }
                }

                @media (max-width: 1120px) {
                    .zild-mega-menu {
                        grid-template-columns: minmax(220px, 0.7fr) minmax(0, 1.4fr);
                    }

                    .zild-mega-menu__note {
                        display: none;
                    }
                }

                .zild-mobile-menu {
                    max-height: calc(100vh - 70px);
                    max-height: calc(100dvh - 70px);
                    overflow-y: auto;
                    overscroll-behavior: contain;
                }

                @media (min-width: 980px) {
                    .zild-mobile-menu { display: none; }
                }

                @media (prefers-reduced-motion: reduce) {
                    .zild-mega-shell,
                    .zild-mega-menu__item {
                        animation: none;
                        opacity: 1;
                        transform: none;
                    }
                }
            `}</style>
        </header>
    );
};

export default SiteHeader;
